import { Button, Drawer, Form, Input, Space } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PermissionResponse, RoleResponse } from '@api/generated/types.gen';

import { permissionNamesToIds } from '../lib';
import { PermissionPicker } from './PermissionPicker';

/** Значения, которые редактор отдаёт наверх при сохранении. */
export type RoleFormValues = {
  name: string;
  description: string;
  permissionIds: string[];
};

type RoleEditorProps = {
  open: boolean;
  mode: 'create' | 'edit';
  role: RoleResponse | null;
  permissions: PermissionResponse[];
  permissionsLoading: boolean;
  saving: boolean;
  onCancel: () => void;
  onSubmit: (values: RoleFormValues) => void;
};

type MetaFields = {
  name: string;
  description: string;
};

/** Имя роли: заглавные латинские буквы, цифры и `_`, начинается с буквы (зеркалит бэкенд-паттерн). */
const NAME_PATTERN = /^[A-Z][A-Z0-9_]*$/;

/**
 * Боковой drawer для создания и редактирования роли.
 *
 * Презентационный: собирает имя, описание и набор прав, отдаёт их через
 * `onSubmit`. Решение, какие эндпоинты дёрнуть (бэк разделяет обновление
 * метаданных и набора прав), принимает родитель — `RbacPage`.
 */
export function RoleEditor({
  open,
  mode,
  role,
  permissions,
  permissionsLoading,
  saving,
  onCancel,
  onSubmit,
}: RoleEditorProps) {
  const { t } = useTranslation(['rbac', 'common', 'errors']);
  const [form] = Form.useForm<MetaFields>();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && role) {
      form.setFieldsValue({ name: role.name ?? '', description: role.description ?? '' });
      setSelectedIds(permissionNamesToIds(role.permissions ?? [], permissions));
    } else {
      form.setFieldsValue({ name: '', description: '' });
      setSelectedIds([]);
    }
  }, [open, mode, role, permissions, form]);

  const onFinish = (values: MetaFields): void => {
    onSubmit({
      name: values.name.trim(),
      description: values.description.trim(),
      permissionIds: selectedIds,
    });
  };

  return (
    <Drawer
      title={mode === 'create' ? t('rbac:editor.createTitle') : t('rbac:editor.editTitle')}
      placement="right"
      width="min(520px, 100vw)"
      open={open}
      onClose={onCancel}
      destroyOnHidden
      footer={
        <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onCancel}>{t('common:actions.cancel')}</Button>
          <Button
            type="primary"
            loading={saving}
            onClick={() => {
              form.submit();
            }}
          >
            {t('common:actions.save')}
          </Button>
        </Space>
      }
    >
      <Form<MetaFields> form={form} layout="vertical" onFinish={onFinish} requiredMark={false}>
        <Form.Item
          label={t('rbac:editor.nameLabel')}
          name="name"
          extra={t('rbac:editor.nameHint')}
          rules={[
            { required: true, message: t('errors:fields.NOT_BLANK') },
            { max: 64, message: t('errors:fields.SIZE') },
            { pattern: NAME_PATTERN, message: t('rbac:editor.nameInvalid') },
          ]}
        >
          <Input
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
            placeholder={t('rbac:editor.namePlaceholder')}
            autoFocus={mode === 'create'}
            maxLength={64}
          />
        </Form.Item>

        <Form.Item
          label={t('rbac:editor.descriptionLabel')}
          name="description"
          rules={[{ max: 512, message: t('errors:fields.SIZE') }]}
        >
          <Input.TextArea
            placeholder={t('rbac:editor.descriptionPlaceholder')}
            autoSize={{ minRows: 2, maxRows: 4 }}
            maxLength={512}
          />
        </Form.Item>

        <Form.Item label={t('rbac:editor.permissionsLabel')} extra={t('rbac:editor.permissionsHint')}>
          <PermissionPicker
            permissions={permissions}
            value={selectedIds}
            onChange={setSelectedIds}
            loading={permissionsLoading}
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
